import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { defineMessages, FormattedMessage } from "react-intl";
import { retrieveDashboardPosts, retrieveWebinar } from "shared-frontend/redux/actions/home";
import getEnv from "shared-frontend/functions/getEnv";
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { Button, Card, Title } from "@yoast/ui-library";
import * as styles from "./WhatsNewCardStyles.module.scss";

const messages = defineMessages( {
	header: {
		id: "home.whatsnew.header",
		defaultMessage: "What's new & Learn",
	},
	webinarHeader: {
		id: "home.whatsnew.webinarHeader",
		defaultMessage: "Upcoming webinar",
	},
	webinarLoading: {
		id: "home.whatsnew.webinarLoading",
		defaultMessage: "Retrieving the upcoming webinar...",
	},
	registerButton: {
		id: "home.whatsnew.registerButton",
		defaultMessage: "Register now!",
	},
	webinarArchiveLink: {
		id: "home.whatsnew.webinarArchiveLink",
		defaultMessage: "All Yoast SEO webinars",
	},
	readMore: {
		id: "home.whatsnew.readMore",
		defaultMessage: "Read more",
	},
	blogHeader: {
		id: "home.whatsnew.blogHeader",
		defaultMessage: "Learn more about SEO",
	},
	blogLoading: {
		id: "home.whatsnew.blogLoading",
		defaultMessage: "Retrieving recent blog posts...",
	},
	blogFooterLink: {
		id: "home.whatsnew.blogFooterLink",
		defaultMessage: "Visit the SEO blog",
	},
} );

/**
 * Renders a single blog post item.
 *
 * @param {Object} props The props.
 * @returns {ReactElement} The blog post item.
 */
const FeedListItem = ( { title, link, image = "" } ) => (
	<li className={ styles.blogItem } data-testid="blog-feed-item">
		{ image
			? <img className={ styles.blogItemImage } src={ image } alt="" />
			: <div className={ styles.blogItemImagePlaceholder } aria-hidden="true" /> }
		<div className={ styles.blogItemContent }>
			<p className={ styles.blogItemTitle }>{ title }</p>
		</div>
		<Button
			as="a"
			href={ link.replace(
				"#utm_source=yoast-seo&utm_medium=software&utm_campaign=wordpress-general&utm_content=wordpress-dashboard",
				"",
			) }
			target="_blank"
			rel="noopener noreferrer"
			variant="secondary"
			className={ styles.readMoreButton }
		>
			<FormattedMessage { ...messages.readMore } />
			<ArrowTopRightOnSquareIcon className={ styles.buttonIcon } />
		</Button>
	</li>
);

FeedListItem.propTypes = {
	link: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	image: PropTypes.string,
};

/**
 * Renders the list of blog posts.
 *
 * @param {Object} props The props.
 * @returns {ReactElement} The blog feed list.
 */
const FeedList = ( props ) => {
	if ( props.retrievingFeed ) {
		return <p className="yst-italic"><FormattedMessage { ...messages.blogLoading } /></p>;
	}
	return (
		<ul className={ styles.blogList }>
			{ props.blogFeed.items.map( item => (
				<FeedListItem
					key={ item.link }
					title={ item.title }
					link={ item.link }
					image={ item.image }
				/>
			) ) }
		</ul>
	);
};

FeedList.propTypes = {
	blogFeed: PropTypes.object.isRequired,
	retrievingFeed: PropTypes.bool.isRequired,
};

/**
 * Renders the upcoming webinar section.
 *
 * @param {Object} props The props.
 * @returns {ReactElement|null} The webinar section, or null when there is no webinar to show.
 */
const WebinarSection = ( { retrievingWebinar, webinar = null } ) => {
	if ( retrievingWebinar ) {
		return <p className="yst-italic"><FormattedMessage { ...messages.webinarLoading } /></p>;
	}

	// No data (e.g. the request failed): render nothing rather than a broken section.
	if ( ! webinar ) {
		return null;
	}

	// A webinar is planned: show its banner and the registration button.
	if ( webinar.found ) {
		const registrationUrl = webinar.registrationLink?.url || webinar.url;

		return (
			<div className={ styles.webinar } data-testid="webinar-banner">
				{ webinar.image && (
					<img className={ styles.webinarImage } src={ webinar.image } alt={ webinar.title } />
				) }
				{ registrationUrl && (
					<Button
						as="a"
						href={ registrationUrl }
						target="_blank"
						rel="noopener noreferrer"
						className={ styles.registerButton }
					>
						<FormattedMessage { ...messages.registerButton } />
						<ArrowTopRightOnSquareIcon className={ styles.buttonIcon } />
					</Button>
				) }
			</div>
		);
	}

	// No webinar planned: point users at the webinar archive.
	return (
		<p>
			{ webinar.title }
			{ " " }
			<a
				href={ webinar.archiveUrl }
				target="_blank"
				rel="noopener noreferrer"
				className={ styles.inlineLink }
			>
				<FormattedMessage { ...messages.webinarArchiveLink } />
				<ArrowTopRightOnSquareIcon className={ styles.buttonIcon } />
			</a>
		</p>
	);
};

WebinarSection.propTypes = {
	retrievingWebinar: PropTypes.bool.isRequired,
	webinar: PropTypes.object,
};

/**
 * Renders the "What's new & Learn" card: the upcoming webinar plus recent blog posts.
 *
 * @param {Object} props The props.
 * @returns {ReactElement} The card.
 */
const WhatsNewContent = ( { getFeed, getWebinar, webinar = null, ...props } ) => {
	useEffect( () => {
		getWebinar();
		getFeed();
	}, [ getWebinar, getFeed ] );

	return (
		<Card>
			<div className={ styles.cardHeader }>
				<Title as="h2" size="4">
					<FormattedMessage { ...messages.header } />
				</Title>
			</div>
			<Card.Content className={ styles.cardContent }>
				<section className={ styles.section }>
					<Title as="h3" size="5">
						<FormattedMessage { ...messages.webinarHeader } />
					</Title>
					<WebinarSection retrievingWebinar={ props.retrievingWebinar } webinar={ webinar } />
				</section>
				<section className={ styles.section }>
					<Title as="h3" size="5">
						<FormattedMessage { ...messages.blogHeader } />
					</Title>
					<FeedList retrievingFeed={ props.retrievingFeed } blogFeed={ props.blogFeed } />
				</section>
			</Card.Content>
			<Card.Footer className={ styles.cardFooter }>
				<a
					href={ getEnv( "SHOP_URL", "https://yoast.test" ) + "/seo-blog/" }
					target="_blank"
					rel="noopener noreferrer"
					className={ styles.footerLink }
				>
					<FormattedMessage { ...messages.blogFooterLink } />
					<ArrowRightIcon className={ styles.footerArrow } />
				</a>
			</Card.Footer>
		</Card>
	);
};

WhatsNewContent.propTypes = {
	getFeed: PropTypes.func.isRequired,
	getWebinar: PropTypes.func.isRequired,
	retrievingFeed: PropTypes.bool.isRequired,
	blogFeed: PropTypes.object.isRequired,
	retrievingWebinar: PropTypes.bool.isRequired,
	webinar: PropTypes.object,
};

/**
 * Maps the state to the props.
 *
 * @param {Object} state The state.
 * @returns {Object} The props.
 */
export const mapStateToProps = ( state ) => {
	const { blogFeed, retrievingFeed, webinar, retrievingWebinar } = state.ui.home;
	return {
		blogFeed,
		retrievingFeed,
		webinar,
		retrievingWebinar,
	};
};

/**
 * Maps the dispatch to the props.
 *
 * @param {Function} dispatch The dispatch.
 * @returns {Object} Object containing the getFeed and getWebinar functions.
 */
export const mapDispatchToProps = ( dispatch ) => ( {
	getFeed: () => dispatch( retrieveDashboardPosts() ),
	getWebinar: () => dispatch( retrieveWebinar() ),
} );

export default connect( mapStateToProps, mapDispatchToProps )( WhatsNewContent );
